import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../api';
import type { CurrencyFormData } from '../types';

const KEYS = {
  all: ['currencies'] as const,
  list: (params?: Record<string, unknown>) => [...KEYS.all, 'list', params] as const,
  active: () => [...KEYS.all, 'active'] as const,
  detail: (id: string) => [...KEYS.all, 'detail', id] as const,
};

export function useCurrencies(params?: { page?: number; limit?: number; search?: string; isActive?: string }) {
  return useQuery({
    queryKey: KEYS.list(params as Record<string, unknown>),
    queryFn: async () => {
      const { data } = await adminApi.getCurrencies(params);
      return data;
    },
  });
}

export function useActiveCurrencies() {
  return useQuery({
    queryKey: KEYS.active(),
    queryFn: async () => {
      const { data } = await adminApi.getActiveCurrencies();
      return data.currencies;
    },
  });
}

export function useCurrencyDetail(id: string | undefined) {
  return useQuery({
    queryKey: KEYS.detail(id!),
    queryFn: async () => {
      const { data } = await adminApi.getCurrencyById(id!);
      return data.currency;
    },
    enabled: !!id,
  });
}

export function useCreateCurrency() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CurrencyFormData) => {
      const { data: result } = await adminApi.createCurrency(data);
      return result.currency;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useUpdateCurrency() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<CurrencyFormData> }) => {
      const { data: result } = await adminApi.updateCurrency(id, data);
      return result.currency;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useDeleteCurrency() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await adminApi.deleteCurrency(id);
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}
